import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, Popover, TextField, Button, Box, FormControl, InputLabel, Select, MenuItem,
  Typography, Divider, Stack
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

function ColumnFilter({ column, currentFilter, setFilter, data }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValue, setFilterValue] = useState('');
  const [filterOperator, setFilterOperator] = useState('contains');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [timeFrom, setTimeFrom] = useState(null);
  const [timeTo, setTimeTo] = useState(null);
  const [datetimeFrom, setDatetimeFrom] = useState(null);
  const [datetimeTo, setDatetimeTo] = useState(null);
  const [numberFrom, setNumberFrom] = useState('');
  const [numberTo, setNumberTo] = useState('');
  const [listValues, setListValues] = useState('');
  const [booleanValue, setBooleanValue] = useState('');

  // Determinar el tipo de filtro basado en el nombre de la columna
  const getFilterType = () => {
    // Si se especifica explícitamente el tipo de filtro, usarlo
    if (column.filterType) {
      return column.filterType;
    }

    const name = column.name?.toLowerCase() || '';
    const label = column.label?.toLowerCase() || '';

    // Detectar fechas
    if (name.includes('date') || name.includes('fecha') ||
        label.includes('date') || label.includes('fecha')) {
      return 'date';
    }

    // Detectar horas
    if (name.includes('time') || name.includes('hora') ||
        label.includes('time') || label.includes('hora')) {
      return 'time';
    }

    // Detectar fecha y hora
    if (name.includes('datetime') || name.includes('created_at') || name.includes('updated_at') ||
        label.includes('datetime') || label.includes('creado') || label.includes('actualizado')) {
      return 'datetime';
    }

    // Detectar números
    if (name.includes('amount') || name.includes('quantity') || name.includes('price') ||
        name.includes('total') || name.includes('count') || name.includes('number') ||
        label.includes('amount') || label.includes('quantity') || label.includes('price') ||
        label.includes('total') || label.includes('count') || label.includes('number')) {
      return 'number';
    }

    // Por defecto es texto
    return 'text';
  };

  const filterType = getFilterType();

  // Función para formatear fecha y hora en zona local
  const formatLocalDateTime = (date) => {
    if (!date) return null;

    // Obtener año, mes, día, hora, minuto en zona local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Formato: YYYY-MM-DD HH:MM:SS
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    // Inicializar valores desde el filtro actual
    if (currentFilter && currentFilter[column.name]) {
      const filter = currentFilter[column.name];
      if (filterType === 'date') {
        setDateFrom(filter.from ? new Date(filter.from) : null);
        setDateTo(filter.to ? new Date(filter.to) : null);
      } else if (filterType === 'time') {
        setTimeFrom(filter.from ? new Date(filter.from) : null);
        setTimeTo(filter.to ? new Date(filter.to) : null);
      } else if (filterType === 'datetime') {
        setDatetimeFrom(filter.from ? new Date(filter.from) : null);
        setDatetimeTo(filter.to ? new Date(filter.to) : null);
      } else if (filterType === 'number') {
        setNumberFrom(filter.from || '');
        setNumberTo(filter.to || '');
      } else if (filterType === 'boolean') {
        setBooleanValue(filter.value || '');
      } else {
        if (filter.operator === 'in') {
          // Filtro de lista
          setListValues(Array.isArray(filter.value) ? filter.value.join(', ') : filter.value);
          setFilterOperator('in');
        } else {
          // Filtro de texto normal
          setFilterValue(filter.value || '');
          setFilterOperator(filter.operator || 'contains');
        }
      }
    } else {
      // Resetear valores
      setFilterValue('');
      setFilterOperator('contains');
      setDateFrom(null);
      setDateTo(null);
      setTimeFrom(null);
      setTimeTo(null);
      setDatetimeFrom(null);
      setDatetimeTo(null);
      setNumberFrom('');
      setNumberTo('');
      setListValues('');
      setBooleanValue('');
    }
  }, [currentFilter, column.name, filterType]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    const newFilters = { ...(currentFilter || {}) };

    let hasValue = false;
    let filterData = {};

    if (filterType === 'date') {
      if (dateFrom || dateTo) {
        hasValue = true;
        filterData = {
          from: dateFrom ? dateFrom.toISOString().split('T')[0] : null, // Solo fecha YYYY-MM-DD
          to: dateTo ? dateTo.toISOString().split('T')[0] : null
        };
      }
    } else if (filterType === 'time') {
      if (timeFrom || timeTo) {
        hasValue = true;
        filterData = {
          from: timeFrom ? timeFrom.toTimeString().split(' ')[0] : null, // Solo hora HH:MM:SS
          to: timeTo ? timeTo.toTimeString().split(' ')[0] : null
        };
      }
    } else if (filterType === 'datetime') {
      if (datetimeFrom || datetimeTo) {
        hasValue = true;
        filterData = {
          from: datetimeFrom ? formatLocalDateTime(datetimeFrom) : null, // Fecha y hora en zona local
          to: datetimeTo ? formatLocalDateTime(datetimeTo) : null
        };
      }
    } else if (filterType === 'number') {
      if (numberFrom || numberTo) {
        hasValue = true;
        filterData = {
          from: numberFrom ? parseFloat(numberFrom) : null,
          to: numberTo ? parseFloat(numberTo) : null
        };
      }
    } else if (filterType === 'boolean') {
      if (booleanValue !== '') {
        hasValue = true;
        // Convertir el valor mostrado al usuario al valor booleano real
        const booleanOptions = column.booleanOptions || { true: 'Sí', false: 'No' };
        const realValue = Object.keys(booleanOptions).find(key => booleanOptions[key] === booleanValue);
        filterData = {
          value: realValue === 'true',
          operator: 'equals'
        };
      }
    } else {
      if (filterOperator === 'in') {
        // Filtro de lista
        const values = listValues.split(',').map(v => v.trim()).filter(v => v.length > 0);
        if (values.length > 0) {
          hasValue = true;
          filterData = {
            value: values,
            operator: 'in'
          };
        }
      } else {
        // Filtro de texto normal
        if (filterValue.trim()) {
          hasValue = true;
          filterData = {
            value: filterValue.trim(),
            operator: filterOperator
          };
        }
      }
    }

    if (hasValue) {
      newFilters[column.name] = filterData;
    } else {
      delete newFilters[column.name];
    }

    setFilter(newFilters);
    handleClose();
  };

  const handleClearFilter = () => {
    const newFilters = { ...(currentFilter || {}) };
    delete newFilters[column.name];
    setFilter(newFilters);
    handleClose();
  };

  const hasActiveFilter = currentFilter && currentFilter[column.name];

  const open = Boolean(anchorEl);
  const id = open ? `filter-popover-${column.name}` : undefined;

  // Renderizar el tipo de filtro apropiado
  const renderFilterContent = () => {
    switch (filterType) {
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Rango de fechas</Typography>
              <DatePicker
                label="Desde"
                value={dateFrom}
                onChange={setDateFrom}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <DatePicker
                label="Hasta"
                value={dateTo}
                onChange={setDateTo}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Stack>
          </LocalizationProvider>
        );

      case 'time':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Rango de horas</Typography>
              <TimePicker
                label="Desde"
                value={timeFrom}
                onChange={setTimeFrom}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <TimePicker
                label="Hasta"
                value={timeTo}
                onChange={setTimeTo}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Stack>
          </LocalizationProvider>
        );

      case 'datetime':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Rango de fecha y hora</Typography>
              <DateTimePicker
                label="Desde"
                value={datetimeFrom}
                onChange={setDatetimeFrom}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <DateTimePicker
                label="Hasta"
                value={datetimeTo}
                onChange={setDatetimeTo}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Stack>
          </LocalizationProvider>
        );

      case 'number':
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Rango de valores</Typography>
            <TextField
              label="Desde"
              type="number"
              value={numberFrom}
              onChange={(e) => setNumberFrom(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Hasta"
              type="number"
              value={numberTo}
              onChange={(e) => setNumberTo(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>
        );

      case 'boolean':
        const booleanOptions = column.booleanOptions || { true: 'Sí', false: 'No' };
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Filtro booleano</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Valor</InputLabel>
              <Select
                value={booleanValue}
                onChange={(e) => setBooleanValue(e.target.value)}
                label="Valor"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {Object.entries(booleanOptions).map(([key, label]) => (
                  <MenuItem key={key} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        );

      default: // text
        return (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Filtro de texto</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Operador</InputLabel>
              <Select
                value={filterOperator}
                onChange={(e) => {
                  setFilterOperator(e.target.value);
                  // Resetear valores cuando cambia el operador
                  if (e.target.value === 'in') {
                    setFilterValue('');
                  } else {
                    setListValues('');
                  }
                }}
                label="Operador"
              >
                <MenuItem value="contains">Contiene</MenuItem>
                <MenuItem value="equals">Igual a</MenuItem>
                <MenuItem value="starts_with">Comienza por</MenuItem>
                <MenuItem value="ends_with">Termina por</MenuItem>
                <MenuItem value="not_contains">No contiene</MenuItem>
                <MenuItem value="in">Lista de valores</MenuItem>
              </Select>
            </FormControl>

            {filterOperator === 'in' ? (
              <TextField
                label="Valores (separados por coma)"
                value={listValues}
                onChange={(e) => setListValues(e.target.value)}
                size="small"
                fullWidth
                placeholder="valor1, valor2, valor3..."
                helperText="Ingrese los valores separados por coma"
                multiline
                rows={3}
              />
            ) : (
              <TextField
                label="Valor"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                size="small"
                fullWidth
                placeholder="Ingrese el valor a buscar..."
              />
            )}
          </Stack>
        );
    }
  };

  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        size="small"
        sx={{
          color: hasActiveFilter ? 'primary.main' : 'inherit',
          '&:hover': {
            color: 'primary.main',
          }
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Filtro: {column.label}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {renderFilterContent()}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button
              onClick={handleClearFilter}
              size="small"
              variant="outlined"
              color="error"
            >
              Limpiar
            </Button>
            <Button
              onClick={handleApplyFilter}
              variant="contained"
              size="small"
            >
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

ColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
  currentFilter: PropTypes.object,
  setFilter: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
};

export default ColumnFilter;